import React, { useState } from 'react';
import { Form, Button, Space, Tooltip, Divider } from 'antd';
import {
  BoldOutlined,
  OrderedListOutlined,
  LinkOutlined,
  EnterOutlined,
  EyeOutlined,
  EditOutlined,
  FontColorsOutlined
} from '@ant-design/icons';
import './editor.css';



const ColoredText = ({ text = '', color = '#1890ff' }) => {
  // Enhanced regex to handle bullet points with formatted text
  // We'll split on these patterns:
  // 1. •**text** - Bullet with bold text
  // 2. •*text* - Bullet with colored text
  // 3. Standard patterns from before
  const parts = text.split(/(•\*\*.*?\*\*|•\*.*?\*|\*#.*?#\*|\*[^*]+\*|#{3}[^#]+#{3}|#{2}[^#]+#{2}|#{1}[^#]+#{1}|\*\*[^*]+\*\*|\n\* [^\n]+|\n)/g);

  return (
    <div className="rich-text-preview">
      {parts.map((part, index) => {
        // Bullet with bold: •**text**
        if (part.startsWith('•**') && part.endsWith('**')) {
          return (
            <div key={index} className="rich-text-bullet">
              <span className="rich-text-bullet-marker">•</span>
              <strong>{part.slice(3, -2)}</strong>
            </div>
          );
        }
        // Bullet with colored text: •*text*
        else if (part.startsWith('•*') && part.endsWith('*')) {
          return (
            <div key={index} className="rich-text-bullet">
              <span className="rich-text-bullet-marker">•</span>
              <span style={{ color }}>{part.slice(2, -1)}</span>
            </div>
          );
        }
        // Links: *#url#*
        else if (part.startsWith('*#') && part.endsWith('#*')) {
          const url = part.slice(2, -2);
          return (
            <a
              key={index}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1890ff" }}
            >
              {url}
            </a>
          );
        } 
        // Colored text: *text*
        else if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
          return (
            <span key={index} style={{ color }}>
              {part.slice(1, -1)}
            </span>
          );
        }
        // H1: ###Heading###
        else if (part.startsWith('###') && part.endsWith('###')) {
          return (
            <h1 key={index} className="rich-text-h1">
              {part.slice(3, -3)}
            </h1>
          );
        }
        // H2: ##Subheading##
        else if (part.startsWith('##') && part.endsWith('##')) {
          return (
            <h2 key={index} className="rich-text-h2">
              {part.slice(2, -2)}
            </h2>
          );
        }
        // H3: #Smaller heading#
        else if (part.startsWith('#') && part.endsWith('#')) {
          return (
            <h3 key={index} className="rich-text-h3">
              {part.slice(1, -1)}
            </h3>
          );
        }
        // Bold: **text**
        else if (part.startsWith('**') && part.endsWith('**')) {
          return (
            <strong key={index}>
              {part.slice(2, -2)}
            </strong>
          );
        }
        // Bullet points: \n* item
        else if (part.startsWith('\n* ')) {
          return (
            <div key={index} className="rich-text-bullet">
              <span className="rich-text-bullet-marker">•</span>
              <span>{part.slice(3)}</span>
            </div>
          );
        }
        // Line breaks: \n
        else if (part === '\n') {
          return <br key={index} />;
        }
        // Normal text
        else {
          return <span key={index}>{part}</span>;
        }
      })}
    </div>
  );
};

const RichTextEditor= ({ 
  initialValue = '', 
  onChange,
  color = '#1890ff',
  value
}) => {
  // Use value from form or local state
  const [innerValue, setInnerValue] = useState(initialValue);
  const effectiveValue = value !== undefined ? value : innerValue;
  
  const [showPreview, setShowPreview] = useState(false);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setInnerValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const insertFormat = (format) => {
    // Get textarea element
    const textarea = document.getElementById('rich-text-area') ;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = effectiveValue.substring(start, end);
    let newText = '';

    switch (format) {
      case 'color':
        newText = effectiveValue.substring(0, start) + '*' + selectedText + '*' + effectiveValue.substring(end);
        break;
      case 'bold':
        newText = effectiveValue.substring(0, start) + '**' + selectedText + '**' + effectiveValue.substring(end);
        break;
      case 'h1':
        newText = effectiveValue.substring(0, start) + '###' + selectedText + '###' + effectiveValue.substring(end);
        break;
      case 'h2':
        newText = effectiveValue.substring(0, start) + '##' + selectedText + '##' + effectiveValue.substring(end);
        break;
      case 'h3':
        newText = effectiveValue.substring(0, start) + '#' + selectedText + '#' + effectiveValue.substring(end);
        break;
      case 'link':
        newText = effectiveValue.substring(0, start) + '*#' + (selectedText || 'url') + '#*' + effectiveValue.substring(end);
        break;
      case 'bullet':
        // Simple bullet
        newText = effectiveValue.substring(0, start) + '\n• ' + selectedText + effectiveValue.substring(end);
        break;
      case 'bulletBold':
        // Bullet with bold text
        newText = effectiveValue.substring(0, start) + '\n•**' + selectedText + '**' + effectiveValue.substring(end);
        break;
      case 'bulletColor':
        // Bullet with colored text
        newText = effectiveValue.substring(0, start) + '\n•*' + selectedText + '*' + effectiveValue.substring(end);
        break;
      case 'break':
        newText = effectiveValue.substring(0, start) + '\n' + effectiveValue.substring(end);
        break;
      default:
        return;
    }

    setInnerValue(newText);
    if (onChange) {
      onChange(newText);
    }

    // Set focus back to textarea
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = format === 'link' && !selectedText ? start + 3 : start + newText.length - effectiveValue.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  return (
    <div className="rich-text-editor">
      <div className="rich-text-toolbar">
        <Space style={{overflowX:"scroll",height:"40px"}}>
          <Button.Group>
            <Tooltip title="Heading 1">
              <Button type="text" onClick={() => insertFormat('h1')}>H1</Button>
            </Tooltip>
            <Tooltip title="Heading 2">
              <Button type="text" onClick={() => insertFormat('h2')}>H2</Button>
            </Tooltip>
            <Tooltip title="Heading 3">
              <Button type="text" onClick={() => insertFormat('h3')}>H3</Button>
            </Tooltip>
          </Button.Group>
          
          <Divider type="vertical" />
          
          <Button.Group>
            <Tooltip title="Bold">
              <Button 
                type="text" 
                icon={<BoldOutlined />} 
                onClick={() => insertFormat('bold')}
              />
            </Tooltip>
            <Tooltip title="Colored Text">
              <Button 
                type="text" 
                icon={<FontColorsOutlined style={{ color: color }} />} 
                onClick={() => insertFormat('color')}
              />
            </Tooltip>
          </Button.Group>
          
          <Divider type="vertical" />
          
          <Button.Group>
            <Tooltip title="Add Link">
              <Button 
                type="text" 
                icon={<LinkOutlined />} 
                onClick={() => insertFormat('link')}
              />
            </Tooltip>
            <Tooltip title="Bullet List">
              <Button 
                type="text" 
                icon={<OrderedListOutlined />} 
                onClick={() => insertFormat('bullet')}
              />
            </Tooltip>
            <Tooltip title="Bold Bullet">
              <Button 
                type="text" 
                onClick={() => insertFormat('bulletBold')}
              >
                •<strong>B</strong>
              </Button>
            </Tooltip>
            <Tooltip title="Colored Bullet">
              <Button 
                type="text" 
                onClick={() => insertFormat('bulletColor')}
              >
                •<span style={{ color }}>C</span>
              </Button>
            </Tooltip>
            <Tooltip title="Line Break">
              <Button 
                type="text" 
                icon={<EnterOutlined />} 
                onClick={() => insertFormat('break')}
              />
            </Tooltip>
          </Button.Group>
        </Space>
        
        <Button 
          type="default"
          icon={showPreview ? <EditOutlined /> : <EyeOutlined />}
          onClick={() => setShowPreview(!showPreview)}
        >
          {showPreview ? 'Edit' : 'Preview'}
        </Button>
      </div>

      <div className="rich-text-content">
        {showPreview ? (
          <div className="rich-text-preview-container">
            <ColoredText text={effectiveValue} color={color} />
          </div>
        ) : (
          <textarea
            id="rich-text-area"
            value={effectiveValue}
            onChange={handleChange}
            placeholder="Type your formatted text here..."
            className="rich-text-textarea"
          />
        )}
      </div>

      <div className="rich-text-help">
        Formatting: *colored text*, **bold**, ###Heading###, ##Subheading##, #Small Heading#, *#link#*, 
        • Bullet point, •**Bold bullet**, •*Colored bullet*
      </div>
    </div>
  );
};

// The form component using the RichTextEditor
const FeedbackForm = () => {
  const [form] = Form.useForm();

  const onFinish = (values) => {
    console.log('Form values:', values);
    // Process your form submission here
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      layout="vertical"
    >
      <Form.Item
        name="feedback"
        label="Feedback"
        rules={[
          {
            required: true,
            message: "Feedback is required!",
          },
        ]}
      >
        <RichTextEditor />
      </Form.Item>
      
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
};

export { ColoredText, RichTextEditor, FeedbackForm };